import { allowBroadcast, disallowBroadcast } from 'features/permissions/actions';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import { selectIsAudioOnlyRoom } from 'features/room/roomSlice';
import { User, UserId } from 'features/users/types';
import { selectUserById } from 'features/users/usersSlice';
import { call, put, select } from 'redux-saga/effects';
import { eventBus } from 'utils/eventBus';
import { hasDynamicPermissionsSaga } from 'features/permissions/sagas/hasDynamicPermissionsSaga';

export function* onManageBroadcastCommandSaga(
  userId: UserId,
  allowConfig: boolean | { audio?: boolean; video?: boolean }
) {
  if (!userId || (typeof userId !== 'string' && typeof userId !== 'number')) {
    yield call(
      eventBus.forbiddenActionError,
      'Invalid user ID provided. User ID must be a valid identifier.',
      'manage-broadcast'
    );
    return;
  }

  const targetUser: User = yield select(selectUserById, userId);
  if (!targetUser) {
    return;
  }

  const canManageBroadcast: boolean = yield call(
    hasPermissionsSaga,
    PermissionTypes.manageBroadcast,
    {
      targetRole: targetUser.role,
    }
  );

  if (!canManageBroadcast) {
    yield call(
      eventBus.forbiddenActionError,
      'Forbidden action. This participant is not allowed to manage broadcast',
      'manage-broadcast'
    );

    return;
  }

  const targetRoleCanBroadcast: boolean = yield call(
    hasPermissionsSaga,
    [PermissionTypes.broadcastAudio, PermissionTypes.broadcastVideo],
    {
      role: targetUser.role,
      mode: 'any',
    }
  );

  if (targetRoleCanBroadcast) {
    // if target users has static permissions granted by role
    // we can't change them
    yield call(
      eventBus.forbiddenActionError,
      'Forbidden action. This participant is not allowed to manage target participant’s broadcast',
      'manage-broadcast'
    );
    return;
  }

  // Check if user already has dynamic broadcast permissions
  if (allowConfig !== false) {
    const hasDynamicBroadcast: boolean = yield call(
      hasDynamicPermissionsSaga,
      [PermissionTypes.broadcastAudio, PermissionTypes.broadcastVideo],
      {
        targetUser,
        mode: 'any',
      }
    );

    if (hasDynamicBroadcast) {
      yield call(
        eventBus.forbiddenActionError,
        'Forbidden action. This participant already has dynamic broadcast permissions. Call disallowBroadcast first.',
        'manage-broadcast'
      );
      return;
    }
  }

  const isAudioOnly: boolean = yield select(selectIsAudioOnlyRoom);

  // Handle both legacy boolean and new object format
  if (typeof allowConfig === 'boolean') {
    if (allowConfig) {
      yield put(
        allowBroadcast({
          id: userId,
          video: !isAudioOnly,
          audio: true,
        })
      );
    } else {
      yield put(disallowBroadcast(userId));
    }
  } else {
    // New format with granular control
    const audio = allowConfig.audio ?? false;
    const video = allowConfig.video ?? false;

    if (isAudioOnly && video) {
      yield call(
        eventBus.forbiddenActionError,
        'Video broadcast permissions cannot be granted in audio-only rooms',
        'manage-broadcast'
      );
      return;
    }

    // Validate that at least one of audio or video is true
    if (!audio && !video) {
      yield call(
        eventBus.forbiddenActionError,
        'Forbidden action. At least one broadcast permission (audio or video) must be enabled',
        'manage-broadcast'
      );
      return;
    }

    yield put(
      allowBroadcast({
        id: userId,
        audio,
        video,
      })
    );
  }
}
